/**
 * Voice-mode field decorator — wraps the Build-2 field components in the
 * 3-state presentation:
 *
 *   captured   — value present and confident → normal filled card
 *   confirm    — in ambiguities[] or its date needsConfirmation → amber border,
 *                shows the HEARD phrase + candidate chips + "Looks right"
 *   missing    — required field absent → dashed/highlighted card
 *
 * Tapping the pencil (or the field body) swaps in the SAME editor Type mode
 * uses; editing clears the confirmation flag (handled by the parent through
 * applyVoiceOps + dismissal).
 */

import { AlertTriangle, Check, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

export type VoiceFieldState = "captured" | "confirm" | "missing";

interface VoiceFieldShellProps {
  label: string;
  state: VoiceFieldState;
  /** Amber: the exact phrase Atlas heard. */
  heard?: string | null;
  /** Amber: what needs confirming / resolving. */
  note?: string | null;
  /** Amber: tappable candidate values (only when actionable). */
  candidates?: string[];
  onCandidate?: (candidate: string) => void;
  /** "Looks right" — confirm the value as-is and clear the flag. */
  onConfirm?: () => void;
  editing: boolean;
  onEditToggle: () => void;
  /** Brief teal glow when Atlas just filled/changed this field (Phase C). */
  pulse?: boolean;
  /** Changes per parse/correction so the pulse restarts on repeat changes. */
  pulseNonce?: number;
  /** Read view. */
  display: ReactNode;
  /** Edit view (the Build-2 component). */
  editor?: ReactNode;
}

export function VoiceFieldShell({
  label,
  state,
  heard,
  note,
  candidates,
  onCandidate,
  onConfirm,
  editing,
  onEditToggle,
  pulse,
  pulseNonce,
  display,
  editor,
}: VoiceFieldShellProps) {
  const canEdit = !!editor;
  return (
    <div
      className={cn(
        "relative rounded-xl border px-3 py-2.5 transition-colors",
        state === "confirm" && "border-[hsl(38_92%_50%/0.55)] bg-[hsl(38_92%_50%/0.07)]",
        state === "missing" && "border-2 border-dashed border-border bg-muted/40",
        state === "captured" && "border-border bg-card",
      )}
    >
      {pulse && (
        <span
          key={pulseNonce}
          aria-hidden
          className="voice-flash-overlay pointer-events-none absolute inset-0 rounded-xl"
        />
      )}
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
        <div className="flex items-center gap-1.5">
          {state === "confirm" && !editing && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(38_92%_50%/0.18)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[hsl(28_95%_38%)]">
              <AlertTriangle className="h-3 w-3" /> Confirm
            </span>
          )}
          {state === "missing" && !editing && (
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
              Needed
            </span>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={onEditToggle}
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors",
                editing
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              <Pencil className="h-3 w-3" />
              {editing ? "Done" : "Edit"}
            </button>
          )}
        </div>
      </div>

      <div className="mt-1.5">
        {editing && editor ? editor : display}
      </div>

      {state === "confirm" && !editing && (heard || note || (candidates && candidates.length > 0)) && (
        <div className="mt-2 space-y-1.5 border-t border-[hsl(38_92%_50%/0.3)] pt-2">
          {heard && (
            <p className="text-[11px] text-muted-foreground">
              Heard <span className="font-medium italic text-foreground">“{heard}”</span>
            </p>
          )}
          {note && <p className="text-[11px] text-[hsl(28_95%_38%)]">{note}</p>}
          <div className="flex flex-wrap items-center gap-1.5">
            {candidates?.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => onCandidate?.(c)}
                className="rounded-full border border-[hsl(38_92%_50%/0.6)] bg-background px-2.5 py-1 text-[11px] font-medium text-foreground transition-colors hover:bg-[hsl(38_92%_50%/0.15)]"
              >
                {c}
              </button>
            ))}
            {onConfirm && (
              <button
                type="button"
                onClick={onConfirm}
                className="inline-flex items-center gap-1 rounded-full border border-emerald-500/50 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 transition-colors hover:bg-emerald-500/20"
              >
                <Check className="h-3 w-3" /> Looks right
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
